import { useEffect, useMemo, useState, FormEvent } from 'react';
import { Landmark, Search, ArrowLeft, ArrowRight, AlertTriangle, Tag, Trash2 } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Modal from '../components/common/Modal';
import { PageLoading } from '../components/common/Loading';

type AccountSummary = {
  account_number: string;
  transaction_count: number;
  last_post_date: string | null;
};

type AccountTransaction = {
  id: number;
  account_number: string;
  post_date: string;
  check_number: string | null;
  description: string;
  debit: number | null;
  credit: number | null;
  status: string | null;
  balance: number | null;
  is_duplicate: number;
};

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export default function Accounts() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');

  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);

  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    search: '',
    duplicates: 'all' as 'all' | 'only' | 'exclude',
  });

  const appliedFilterCount = useMemo(() => {
    return [
      filters.start_date,
      filters.end_date,
      filters.search,
      filters.duplicates !== 'all' ? 'duplicates' : '',
    ].filter(Boolean).length;
  }, [filters]);

  useEffect(() => {
    void loadAccounts();
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    void loadAccountTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, page, filters.start_date, filters.end_date, filters.search, filters.duplicates]);

  const loadAccounts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<{ accounts: AccountSummary[] }>('/accounts');
      const list = response.accounts || [];
      setAccounts(list);

      if (list.length > 0) {
        setSelectedAccount((prev) => prev || list[0].account_number);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccountTransactions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, meta: apiMeta } = await api.getPaginated<AccountTransaction>(
        `/accounts/${encodeURIComponent(selectedAccount)}/transactions`,
        page,
        25,
        {
          params: {
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
            search: filters.search || undefined,
            duplicates: filters.duplicates,
          },
        }
      );

      setTransactions(data);

      if (!apiMeta) {
        setMeta(null);
        return;
      }

      setMeta({
        page: apiMeta.page ?? page,
        limit: apiMeta.limit ?? 25,
        total: apiMeta.total ?? 0,
        totalPages: apiMeta.totalPages ?? 1,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load account transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const applySearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchDraft.trim() }));
  };

  const clearFilters = () => {
    setSearchDraft('');
    setPage(1);
    setFilters({ start_date: '', end_date: '', search: '', duplicates: 'all' });
  };

  const [confirmDelete, setConfirmDelete] = useState<AccountTransaction | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<AccountTransaction | null>(null);
  const [confirmDeleteDuplicates, setConfirmDeleteDuplicates] = useState(false);

  const toggleDuplicateFlag = async (tx: AccountTransaction) => {
    try {
      const next = tx.is_duplicate === 0;
      const updated = { ...tx, is_duplicate: next ? 1 : 0 };
      await api.patch(`/accounts/transactions/${tx.id}`, { is_duplicate: next });
      setTransactions((prev) => prev.map((row) => (row.id === tx.id ? updated : row)));
      setSelectedTransaction((prev) => (prev && prev.id === tx.id ? updated : prev));
    } catch (err: any) {
      setError(err.message || 'Failed to update duplicate flag');
    }
  };

  const deleteTransaction = async (tx: AccountTransaction) => {
    try {
      await api.delete(`/accounts/transactions/${tx.id}`);
      setConfirmDelete(null);
      setSelectedTransaction(null);
      await loadAccountTransactions();
      await loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete transaction');
    }
  };

  const deleteDuplicates = async () => {
    if (!selectedAccount) return;

    try {
      await api.delete(`/accounts/${encodeURIComponent(selectedAccount)}/duplicates`);
      setConfirmDeleteDuplicates(false);
      await loadAccountTransactions();
      await loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete duplicates');
    }
  };

  if (isLoading && accounts.length === 0) return <PageLoading />;

  const selectedMeta = accounts.find((a) => a.account_number === selectedAccount);

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Accounts"
        subtitle="Review imported bank-statement transactions"
        actions={
          <Badge variant="default">
            {selectedAccount ? `Account ${selectedAccount}` : accounts.length === 0 ? 'No accounts' : 'Select an account'}
          </Badge>
        }
      />

      {error && (
        <div className="p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <Card title="Account" subtitle={selectedMeta ? `${selectedMeta.transaction_count} imported rows` : undefined}>
        {accounts.length === 0 ? (
          <div className="text-center py-6">
            <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
              <Landmark className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-header">No imported accounts yet</h3>
            <p className="mt-2 text-sm text-text-muted">Import a CSV to see account activity here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">Account Number</label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setPage(1);
                  setSelectedAccount(e.target.value);
                }}
                className="input-field"
              >
                {accounts.map((a) => (
                  <option key={a.account_number} value={a.account_number}>
                    {a.account_number}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">Duplicates</label>
              <select
                value={filters.duplicates}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, duplicates: e.target.value as typeof filters.duplicates }));
                }}
                className="input-field"
              >
                <option value="all">All</option>
                <option value="only">Only marked duplicates</option>
                <option value="exclude">Exclude marked duplicates</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">Post Date</label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({ ...prev, start_date: e.target.value }));
                  }}
                  className="input-field"
                />
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({ ...prev, end_date: e.target.value }));
                  }}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">Search Description</label>
              <form onSubmit={applySearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                  <input
                    type="text"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    className="input-field pl-9"
                    placeholder="Coffee, payroll…"
                  />
                </div>
                <button type="submit" className="btn btn-secondary">
                  Apply
                </button>
              </form>
            </div>

            {appliedFilterCount > 0 && (
              <div className="md:col-span-4 flex justify-end">
                <button onClick={clearFilters} className="btn btn-secondary">
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </Card>

      {accounts.length > 0 && selectedAccount && (
        <Card
          title="Imported Transactions"
          subtitle={meta ? `${meta.total} total` : undefined}
          actions={
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteDuplicates(true)}
                disabled={isLoading}
                className="btn btn-danger"
              >
                Delete marked duplicates
              </button>
              <Badge variant="default">{isLoading ? 'Loading…' : `${transactions.length} shown`}</Badge>
            </div>
          }
        >
          {transactions.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-text-muted">No imported transactions match your filters.</p>
            </div>
          ) : (
            <div className="-mx-6 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-border bg-background-card/50">
                    <th className="table-header">Account</th>
                    <th className="table-header">Post Date</th>
                    <th className="table-header">Description</th>
                    <th className="table-header text-right hidden md:table-cell">Debit</th>
                    <th className="table-header text-right hidden md:table-cell">Credit</th>
                    <th className="table-header hidden md:table-cell">Status</th>
                    <th className="table-header text-right hidden md:table-cell">Balance</th>
                    <th className="table-header hidden md:table-cell">Duplicate</th>
                    <th className="table-header text-right hidden md:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {transactions.map((t) => (
                    <tr
                      key={t.id}
                      className="table-row cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedTransaction(t)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedTransaction(t);
                        }
                      }}
                    >
                      <td className="table-cell text-text-header">{t.account_number}</td>
                      <td className="table-cell text-text-muted">{t.post_date}</td>
                      <td className="table-cell text-text-header">
                        <div className="max-w-[16rem] truncate md:max-w-none">{t.description}</div>
                      </td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">{t.debit === null ? '—' : formatCurrency(t.debit)}</td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">{t.credit === null ? '—' : formatCurrency(t.credit)}</td>
                      <td className="table-cell text-text-muted hidden md:table-cell">{t.status ?? '—'}</td>
                      <td className="table-cell text-right text-text-header hidden md:table-cell">{t.balance === null ? '—' : formatCurrency(t.balance)}</td>
                      <td className="table-cell hidden md:table-cell">
                        {t.is_duplicate ? (
                          <Badge variant="warning">Marked</Badge>
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </td>
                      <td className="table-cell text-right hidden md:table-cell">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleDuplicateFlag(t);
                            }}
                            title={t.is_duplicate ? 'Unmark duplicate' : 'Mark duplicate'}
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger px-3 py-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(t);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="text-sm text-text-muted">
                Showing {(meta.page - 1) * meta.limit + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= meta.totalPages}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Modal
        isOpen={Boolean(selectedTransaction)}
        onClose={() => setSelectedTransaction(null)}
        title="Imported transaction"
        description={selectedTransaction ? `${selectedTransaction.account_number} • ${selectedTransaction.post_date}` : undefined}
        size="lg"
        footer={
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={() => setSelectedTransaction(null)} className="btn btn-secondary">
              Close
            </button>
            <div className="flex items-center justify-end gap-2">
              {selectedTransaction && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary px-3 py-2"
                    onClick={() => void toggleDuplicateFlag(selectedTransaction)}
                    title={selectedTransaction.is_duplicate ? 'Unmark duplicate' : 'Mark duplicate'}
                  >
                    <Tag className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-3 py-2"
                    onClick={() => setConfirmDelete(selectedTransaction)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        }
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailField label="Account" value={selectedTransaction.account_number} />
              <DetailField label="Post Date" value={selectedTransaction.post_date} />
              <DetailField label="Debit" value={selectedTransaction.debit === null ? '' : formatCurrency(selectedTransaction.debit)} />
              <DetailField label="Credit" value={selectedTransaction.credit === null ? '' : formatCurrency(selectedTransaction.credit)} />
              <DetailField label="Status" value={selectedTransaction.status ?? ''} />
              <DetailField label="Balance" value={selectedTransaction.balance === null ? '' : formatCurrency(selectedTransaction.balance)} />
              <DetailField label="Duplicate Tag" value={selectedTransaction.is_duplicate ? 'Marked' : ''} />
            </div>
            <DetailField label="Description" value={selectedTransaction.description} />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && void deleteTransaction(confirmDelete)}
        title="Delete imported transaction"
        message={confirmDelete ? `Delete “${confirmDelete.description}” on ${confirmDelete.post_date}?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={confirmDeleteDuplicates}
        onClose={() => setConfirmDeleteDuplicates(false)}
        onConfirm={() => void deleteDuplicates()}
        title="Delete marked duplicates"
        message={selectedAccount ? `Delete all rows currently marked as duplicates for account ${selectedAccount}?` : 'Delete all rows currently marked as duplicates?'}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  const display = value || '\u00A0';

  return (
    <div className="rounded-xl border border-border bg-background-subtle/30 p-4">
      <div className="text-xs font-medium text-text-muted">{label}</div>
      <div className="mt-1 text-sm text-text-header break-words">{display}</div>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
